import { IUpdateAnnouncement, IUpdateAnnouncementFeed, IUpdateContentAction, IUpdateContentBlock, IUpdateContentImage, TypeUpdateVideoProvider } from './update-announcement.interface';


// cSpell:ignore youtu

export class UpdateAnnouncementFeedHelper {

	/**------------------------------------------------------
	 * Feed Validation
	 */
	static parseDocument(value: unknown, now: number = Date.now()): IUpdateAnnouncementFeed {
		const empty: IUpdateAnnouncementFeed = { enabled: false, bannerEnabled: false, badgeEnabled: false, displayLimit: 1, updates: [] };
		// Existing array feeds continue to work during rollout.
		const document: Record<string, unknown> | null = Array.isArray(value) ? { updates: value } : this.getRecord(value);
		if (!document || !Array.isArray(document.updates) || ('schemaVersion' in document && document.schemaVersion !== 1)) return empty;
		if (['enabled', 'bannerEnabled', 'badgeEnabled'].some((key) => key in document && typeof document[key] !== 'boolean')) return empty;
		const displayLimit: unknown = 'displayLimit' in document ? document.displayLimit : 1;
		if (displayLimit !== null && (typeof displayLimit !== 'number' || !Number.isSafeInteger(displayLimit) || displayLimit < 0)) return empty;
		return {
			enabled: document.enabled !== false,
			bannerEnabled: document.bannerEnabled !== false,
			badgeEnabled: document.badgeEnabled !== false,
			displayLimit: typeof displayLimit === 'number' ? displayLimit : null,
			updates: document.enabled === false ? [] : this.parseFeed(document.updates, now)
		};
	}

	static parseFeed(value: unknown, now: number = Date.now()): IUpdateAnnouncement[] {
		if (!Array.isArray(value)) return [];
		const updates: IUpdateAnnouncement[] = [];
		const ids: Set<string> = new Set<string>();
		for (const item of value) {
			const update: Record<string, unknown> | null = this.getRecord(item);
			if (!update || update.enabled === false || ('schemaVersion' in update && update.schemaVersion !== 1)) continue;
			const id: string = this.getText(update.id);
			const title: string = this.getText(update.title);
			const publishedAt: string = this.getText(update.publishedAt);
			if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(publishedAt)) continue;
			const publishedTime: number = Date.parse(publishedAt);
			if (!id || !title || ids.has(id) || !Number.isFinite(publishedTime) || publishedTime > now || !Array.isArray(update.blocks)) continue;
			const blocks: IUpdateContentBlock[] = update.blocks.map((blockValue: unknown) => this.parseBlock(blockValue)).filter((block): block is IUpdateContentBlock => Boolean(block));
			const summary: string = this.getText(update.summary);
			if (!blocks.length && !summary) continue;
			ids.add(id);
			updates.push({ id, title, publishedAt, summary, blocks, badgeLabel: this.getText(update.badgeLabel) || title, mascotUrl: this.readUrl(update.mascotUrl, true) || '' });
		}
		return updates.sort((first: IUpdateAnnouncement, second: IUpdateAnnouncement) => Date.parse(second.publishedAt) - Date.parse(first.publishedAt));
	}


	/**------------------------------------------------------
	 * URL Validation
	 */
	static readUrl(value: unknown, allowAppPaths: boolean = false): string | null {
		const url: string = this.getText(value);
		if (!url || url.includes('\\') || Array.from(url).some((character: string) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return null;
		if (allowAppPaths && url.startsWith('/') && !url.startsWith('//')) return url;
		try {
			const parsed: URL = new URL(url);
			return parsed.protocol === 'https:' && !parsed.username && !parsed.password ? parsed.href : null;
		} catch {
			return null;
		}
	}


	/**------------------------------------------------------
	 * Content Validation
	 */
	private static parseBlock(value: unknown): IUpdateContentBlock | null {
		const block: Record<string, unknown> | null = this.getRecord(value);
		if (!block) return null;
		switch (block.type) {
			case 'heading': {
				const level: number = 'level' in block ? Number(block.level) : 2;
				return this.getText(block.text) && Number.isInteger(level) && level >= 1 && level <= 6 ? { type: 'heading', text: this.getText(block.text), level } : null;
			}
			case 'paragraph':
			case 'quote':
			case 'code':
				return this.getText(block.text) ? { type: block.type, text: block.text as string, caption: this.getText(block.caption), language: this.getText(block.language) } : null;
			case 'list': {
				const items: string[] = Array.isArray(block.items) ? block.items.map((itemValue: unknown) => this.getText(itemValue)).filter(Boolean) : [];
				return items.length ? { type: 'list', style: block.style === 'numbered' ? 'numbered' : 'bulleted', items } : null;
			}
			case 'link': {
				const action: IUpdateContentAction | null = this.parseAction(block);
				return action ? { type: 'link', label: action.label, url: action.url } : null;
			}
			case 'table': {
				const headers: string[] = Array.isArray(block.headers) && block.headers.every((cell) => typeof cell === 'string') ? block.headers.map((headerValue: unknown) => this.getText(headerValue)) : [];
				const rows: string[][] = Array.isArray(block.rows) ? block.rows.filter((row): row is string[] => Array.isArray(row) && row.length === headers.length && row.every((cell) => typeof cell === 'string')) : [];
				return headers.length && rows.length ? { type: 'table', headers, rows } : null;
			}
			case 'markdown':
				return this.getText(block.markdown) ? { type: 'markdown', markdown: block.markdown as string } : null;
			case 'image': {
				const image: IUpdateContentImage | null = this.parseImage(block);
				return image ? { type: 'image', ...image } : null;
			}
			case 'gallery': {
				const images: IUpdateContentImage[] = Array.isArray(block.images) ? block.images.map((imageValue: unknown) => this.parseImage(imageValue)).filter((image): image is IUpdateContentImage => Boolean(image)) : [];
				return images.length ? { type: 'gallery', images } : null;
			}
			case 'video': {
				const url: string | null = this.readUrl(block.url);
				if (!url) return null;
				const provider: TypeUpdateVideoProvider = ['youtube', 'file', 'link'].includes(this.getText(block.provider)) ? block.provider as TypeUpdateVideoProvider : 'link';
				const id: string | null = provider === 'youtube' ? this.getYoutubeId(url) : null;
				if (provider === 'youtube' && !id) return null;
				return {
					type: 'video', provider, url, youtubeId: id || '',
					title: this.getText(block.title) || 'Watch video', caption: this.getText(block.caption),
					thumbnail: this.readUrl(block.thumbnail, true) || (id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : '')
				};
			}
			case 'callout':
				return this.getText(block.text) || this.getText(block.markdown)
					? {
						type: 'callout', title: this.getText(block.title), text: this.getText(block.text), markdown: typeof block.markdown === 'string' ? block.markdown : '',
						variant: block.variant === 'success' || block.variant === 'warning' ? block.variant : 'info'
					}
					: null;
			case 'actions': {
				const actions: IUpdateContentAction[] = Array.isArray(block.actions) ? block.actions.map((actionValue: unknown) => this.parseAction(actionValue)).filter((action): action is IUpdateContentAction => Boolean(action)) : [];
				return actions.length ? { type: 'actions', actions } : null;
			}
			case 'divider': return { type: 'divider' };
			default: return null;
		}
	}

	private static parseImage(value: unknown): IUpdateContentImage | null {
		const image: Record<string, unknown> | null = this.getRecord(value);
		if (!image) return null;

		const src: string | null = this.readUrl(image.src, true);
		return src ? { src, alt: this.getText(image.alt), caption: this.getText(image.caption) } : null;
	}

	private static parseAction(value: unknown): IUpdateContentAction | null {
		const action: Record<string, unknown> | null = this.getRecord(value);
		if (!action) return null;

		const url: string | null = this.readUrl(action.url, true);
		const label: string = this.getText(action.label);
		return url && label ? { url, label, variant: action.variant === 'secondary' ? 'secondary' : 'primary' } : null;
	}

	private static getYoutubeId(url: string): string | null {
		const parsed: URL = new URL(url);
		let id: string | null = null;
		if (parsed.hostname === 'youtu.be') id = parsed.pathname.slice(1);
		else if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'www.youtube-nocookie.com'].includes(parsed.hostname)) {
			id = parsed.pathname === '/watch' ? parsed.searchParams.get('v') : parsed.pathname.split('/')[2];
		}
		return id && /^[\w-]{11}$/.test(id) ? id : null;
	}


	/**------------------------------------------------------
	 * Value Helpers
	 */
	private static getRecord(value: unknown): Record<string, unknown> | null {
		return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
	}

	private static getText(value: unknown): string {
		return typeof value === 'string' ? value.trim() : '';
	}
}
